// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { RefObject, useCallback, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { SetRailViewAction } from '../projectListActions';
import { IMountedInChunks } from '../useMountedInChunks';

interface IRailScrollPositionOptions {
  // The rail's scrolling list box.
  listRef: RefObject<HTMLElement>;
  // The cards in rail order, so a selection can be found by index.
  projects: IProjectMapCoordinate[];
  mounted: IMountedInChunks;
}

// Where the rail's list sits, across the two things that move it: a return from a project page
// lands the list where the user left it, and a pin picked on the map centres its card.
//
// The return restores first and synchronously - the depth the user had reached is mounted from
// the first commit (useMountedInChunks starts at railView.mountedCount), so the saved offset
// has cards under it - and the centring, which scrolls smoothly, runs after it. Ordered the
// other way round, the restore would cancel the centring and leave the picked card off screen.
export const useRailScrollPosition = ({ listRef, projects, mounted }: IRailScrollPositionOptions) => {
  const dispatch = useAppDispatch();
  const railView = useAppSelector((state) => state.projectList.railView);
  const selectedPinProjectId = useAppSelector((state) => state.projectList.selectedPinProjectId);
  const sortFilter = useAppSelector((state) => state.projectList.sortFilter);
  const mapCoordinatesKey = useAppSelector((state) => state.projectList.mapCoordinatesKey);
  const { mountedCount, ensureMounted, mountedCountRef } = mounted;

  // Restore on mount; capture on unmount (a route navigation). Captured through the node held
  // at mount: React detaches the ref before unmount cleanups run, so reading listRef.current
  // in the cleanup always yields null and a scrollTop of 0.
  useEffect(() => {
    const listNode = listRef.current;
    if (railView?.scrollTop && listNode) listNode.scrollTop = railView.scrollTop;
    return () => {
      dispatch(SetRailViewAction({
        scrollTop: listNode?.scrollTop || 0,
        mountedCount: mountedCountRef.current,
      }));
    };
  }, []);

  // Selections made by clicking a card skip the centring - the user is already looking at it,
  // and re-centring would drag the list out from under the cursor.
  const selectionFromRailRef = useRef(false);
  const markSelectionFromRail = useCallback(() => {
    selectionFromRailRef.current = true;
  }, []);

  // Centres the selected card when the selection changes (map-side) or the list changes under
  // it - a sort re-ordering, or a filter/search refetch landing (mapCoordinatesKey stamps each
  // fetched set, so the re-centring runs against the new list, not the dropdown click) - and
  // never when chunks merely mount during scrolling, which would yank the list back to the
  // selection mid-scroll. A selection filtered out of the set simply does not centre;
  // switching back to a set that contains it centres again.
  const lastCenteredRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedPinProjectId) return;
    const centerContext = `${selectedPinProjectId}|${sortFilter}|${mapCoordinatesKey}`;
    if (selectionFromRailRef.current) {
      // The user clicked this card; no scroll, but this context counts as centred so chunk
      // mounts don't scroll later.
      selectionFromRailRef.current = false;
      lastCenteredRef.current = centerContext;
      return;
    }
    if (lastCenteredRef.current === centerContext) return;
    const selectedIndex = projects.findIndex((project) => project._id === selectedPinProjectId);
    if (selectedIndex === -1) return;
    // A card past the mounted depth is mounted first; this effect re-runs once mountedCount
    // grows and then scrolls. Optional call: jsdom has no scrollIntoView.
    if (!ensureMounted(selectedIndex)) return;
    lastCenteredRef.current = centerContext;
    const card = listRef.current?.querySelector(`[data-project-id="${selectedPinProjectId}"]`);
    card?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [selectedPinProjectId, sortFilter, mapCoordinatesKey, mountedCount]);

  return { markSelectionFromRail };
};

export default useRailScrollPosition;
